import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Something went wrong, please try again.');
    }
    setBusy(false);
  };

  return (
    <div className="app-container min-h-screen bg-[#F5F5F0]" data-testid="forgot-password-page">
      <div className="px-6 pt-12 pb-8">
        <button onClick={() => navigate('/login')} className="mb-8 text-[#6B6E73]" data-testid="back-to-login">
          <ArrowLeft size={22} />
        </button>
        <h1 className="text-3xl font-black tracking-tight text-[#111111]">Forgot your password?</h1>
        <p className="text-sm text-[#6B6E73] mt-2 leading-relaxed">
          Enter the email you signed up with. We&apos;ll send you a secure link to choose a new password.
        </p>

        {sent ? (
          <div className="mt-8 bg-white border border-[#B6E600]/30 rounded-2xl p-5" data-testid="forgot-password-sent">
            <CheckCircle2 size={28} className="text-[#3F5200] mb-3" />
            <p className="text-sm font-bold text-[#111111]">Check your inbox</p>
            <p className="text-xs text-[#6B6E73] mt-1 leading-relaxed">
              If <strong>{email}</strong> matches an account on ASAP, a reset link is on its way. The link expires in 60 minutes.
            </p>
            <Link to="/login" data-testid="back-to-login-cta" className="block mt-5 text-center text-sm font-bold text-[#3F5200] underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-3.5">
            {err && (
              <div data-testid="forgot-error" className="bg-red-50 border border-red-100 rounded-2xl px-4 py-3 text-red-600 text-sm">{err}</div>
            )}
            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#B0B3B8]" />
              <input
                data-testid="forgot-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full bg-white text-[#111111] rounded-2xl pl-11 pr-4 py-[14px] text-sm border border-[#E8E8E8] focus:outline-none focus:border-[#B6E600] focus:shadow-[0_0_0_3px_rgba(182,230,0,0.12)] transition-all"
              />
            </div>
            <button
              data-testid="forgot-submit"
              type="submit"
              disabled={busy || !email.trim()}
              className="w-full bg-[#B6E600] text-[#111111] font-bold rounded-full py-[14px] text-sm flex items-center justify-center gap-2 active:scale-[0.97] disabled:opacity-60 shadow-[0_2px_12px_rgba(182,230,0,0.25)]"
            >
              {busy ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : 'Send reset link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
